import { Eye, EyeOff, LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import HaloBackground from '@/components/HaloBackground';
import Translator, { useTranslation } from 'components/i18n/Translator';

import Alert from './Alert';
import Logo from './Logo';
import { ProviderButton } from './ProviderButton';
import LanguageSelector from './LanguageSelector';
import {
  normalizeAppLanguage,
  storeAppLanguage,
  type AppLanguage
} from '@/lib/language';

interface Props {
  error?: string;
  configLoading?: boolean;
  providers: string[];
  callbackUrl: string;
  onPasswordSignIn?: (
    email: string,
    password: string,
    callbackUrl: string
  ) => Promise<any>;
  onOAuthSignIn?: (provider: string, callbackUrl: string) => Promise<any>;
}

interface FormValues {
  email: string;
  password: string;
}

export function LoginForm({
  providers,
  onPasswordSignIn,
  onOAuthSignIn,
  callbackUrl,
  error,
  configLoading = false
}: Props) {
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(error);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  // GitHub is a workspace connector, not a working login provider for FonGPT.
  const loginProviders = providers.filter((provider) => provider !== 'github');

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: ''
    }
  });

  useEffect(() => {
    setErrorState(error);
  }, [error]);

  const onSubmit = async (data: FormValues) => {
    if (!onPasswordSignIn) return;

    setLoading(true);
    try {
      await onPasswordSignIn(data.email, data.password, callbackUrl);
    } catch (err) {
      if (err instanceof Error) {
        setErrorState(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const oAuthReady = onOAuthSignIn && loginProviders.length;

  const selectLanguage = (language: AppLanguage) => {
    storeAppLanguage(language);
    void i18n.changeLanguage(language);
  };

  return (
    <div className="login-shell min-h-screen overflow-hidden bg-background text-primary">
      <HaloBackground />

      <header className="absolute inset-x-0 top-0 z-20 flex justify-end px-5 py-5 sm:px-8 md:px-10">
        <LanguageSelector
          value={normalizeAppLanguage(i18n.resolvedLanguage || i18n.language)}
          onChange={selectLanguage}
          className="login-language-toggle h-11 w-11 rounded-xl"
        />
      </header>

      <main className="relative z-10 mx-auto flex min-h-screen w-full max-w-[515px] items-center px-6 py-24 sm:px-8">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="login-content w-full text-center"
        >
          <div className="mb-10 flex justify-center sm:mb-12">
            <Logo height={286} variant="auto" />
          </div>

          <h1 className="mb-10 text-[25px] font-semibold leading-tight tracking-[-0.02em] text-primary sm:text-[29px]">
            <Translator path="auth.login.title" />
          </h1>

          {errorState && (
            <div className="mb-5 text-left">
              <Alert variant="error">
                {t([
                  `auth.login.errors.${errorState.toLowerCase()}`,
                  `auth.login.errors.default`
                ])}
              </Alert>
            </div>
          )}

          <div className="space-y-3">
            {configLoading ? (
              <div
                className="flex min-h-28 items-center justify-center gap-2 text-sm text-secondary"
                role="status"
              >
                <LoaderCircle aria-hidden="true" className="h-5 w-5 animate-spin" />
                <span>{t('common.status.loading')}</span>
              </div>
            ) : null}

            {oAuthReady ? (
              loginProviders.map((provider, index) => (
                <ProviderButton
                  key={`provider-${index}`}
                  provider={provider}
                  disabled={loadingProvider !== null}
                  loading={loadingProvider === provider}
                  onClick={() => {
                    setLoadingProvider(provider);
                    void onOAuthSignIn?.(provider, callbackUrl).catch((err) => {
                      setLoadingProvider(null);
                      setErrorState(err instanceof Error ? err.message : 'default');
                    });
                  }}
                />
              ))
            ) : null}

            {onPasswordSignIn && oAuthReady ? (
                <div className="relative py-2 text-center text-sm">
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex-grow h-px bg-outline-variant/50" />
                    <span className="whitespace-nowrap text-xs text-secondary">
                      <Translator path="auth.login.form.alternativeText.or" />
                    </span>
                    <div className="flex-grow h-px bg-outline-variant/50" />
                  </div>
                </div>
            ) : null}

            {onPasswordSignIn && (
              <div className="space-y-4 text-left">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-medium text-primary">
                      <Translator path="auth.login.form.email.label" />
                    </Label>
                    <Input
                      id="email"
                      autoFocus
                      placeholder="me@example.com"
                      {...register('email', {
                        required: t('auth.login.form.email.required')
                      })}
                      className="h-11 rounded-lg border-outline bg-background px-3 text-primary"
                    />
                    {touchedFields.email && errors.email && (
                      <p className="text-sm text-error">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-sm font-medium text-primary">
                      <Translator path="auth.login.form.password.label" />
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        {...register('password', {
                          required: t('auth.login.form.password.required')
                        })}
                        className="h-11 rounded-lg border-outline bg-background px-3 pr-11 text-primary"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="fongpt-hover-text absolute right-2 top-1/2 -translate-y-1/2 text-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {touchedFields.password && errors.password && (
                      <p className="text-sm text-error">{errors.password.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-11 w-full rounded-lg bg-primary font-semibold text-background hover:bg-primary-hover disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <LoaderCircle aria-hidden="true" className="h-5 w-5 animate-spin" />
                        <span>{t('auth.login.form.actions.signingIn')}</span>
                      </>
                    ) : (
                      <Translator path="auth.login.form.actions.signin" />
                    )}
                  </Button>
              </div>
            )}
          </div>

        </form>
      </main>
    </div>
  );
}
